/**
 * Shared boilerplate for Payload's `live-preview` `requestHandler` option.
 * Payload's default handler uses `credentials: 'include'`; all of our handlers
 * omit credentials so public read works with local/dev CORS, then re-fetch the
 * doc as a GET (via method override) so drafts/relations resolve server-side.
 *
 * @param {(args: { serverURL: string, apiPath: string, docId: string|number }) => string} buildUrl
 *   Builds the collection URL (with any query params) for the live-edited doc.
 * @returns {(args: import('@payloadcms/live-preview').CollectionPopulationRequestHandlerArgs) => Promise<Response>}
 */
export function createLivePreviewRequestHandler(buildUrl) {
  return async function requestHandler({ apiPath, data, serverURL }) {
    const incoming = data?.data;
    const docId = incoming?.id;

    if (!docId) {
      return new Response(JSON.stringify(incoming ?? {}), {
        status: 200,
        headers: { "Content-Type": "application/json" },
      });
    }

    let url = buildUrl({ serverURL, apiPath, docId });
    // Payload puts the admin locale on the POST body, not the URL. The refetch
    // is a GET override, so without this query French preview stays on English.
    const locale = data?.locale;
    if (typeof locale === "string" && locale) {
      const hashAt = url.indexOf("#");
      const hash = hashAt >= 0 ? url.slice(hashAt) : "";
      const withoutHash = hashAt >= 0 ? url.slice(0, hashAt) : url;
      url = `${withoutHash}${withoutHash.includes("?") ? "&" : "?"}locale=${encodeURIComponent(locale)}${hash}`;
    }
    return fetch(url, {
      body: JSON.stringify(data),
      headers: {
        "Content-Type": "application/json",
        "X-Payload-HTTP-Method-Override": "GET",
      },
      method: "POST",
    });
  };
}
